import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  PlantItemCreate,
  PlantItemDelete,
  PlantItemInitialState,
  PlantItemLoad,
  PlantItemUpdate,
} from './plant-item.actions';
import { PlantDetailsModel, PlantModel } from '../models';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { PartnerItemInitialState } from '../../partner/item/partner-item.actions';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { NotificationService } from '../../../../../common/services';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants';
import { PlantItemService } from './plant-item.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface PlantItemStateModel extends PlantDetailsModel {
  loaded: boolean;
}

const initialState: PlantItemStateModel = {
  ...new PlantDetailsModel(),
  loaded: false,
};

@State<PlantItemStateModel>({
  name: 'plantItem',
  defaults: initialState,
})
@Injectable()
export class PlantItemState {
  constructor(
    private plantItemService: PlantItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
  ) {}
  @Selector()
  static getState(state: PlantItemStateModel): PlantItemStateModel {
    return state;
  }
  @Selector()
  static getPlantId(state: PlantItemStateModel): number {
    return state.id;
  }
  @Selector()
  static getPlantName(state: PlantItemStateModel): string {
    return state.fullName;
  }
  @Selector()
  static getLoaded(state: PlantItemStateModel): boolean {
    return state.loaded;
  }

  @Action(PlantItemLoad)
  getPlantDetails(
    ctx: StateContext<PlantItemStateModel>,
    { payload }: PlantItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | PlantItemStateModel> {
    return this.plantItemService.getPlantDetails(payload).pipe(
      take(1),
      map((plantModel: PlantModel) => ctx.patchState({ ...plantModel, loaded: true })),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/plants');
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(PlantItemCreate)
  createPlant(
    ctx: StateContext<PlantItemStateModel>,
    { payload }: PlantItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantItemService.createPlant(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.plant.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/plants');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PlantItemUpdate)
  updatePlant(
    ctx: StateContext<PlantItemStateModel>,
    { payload }: PlantItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantItemService.updatePlant(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.plant.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PlantItemDelete)
  deletePlant(
    ctx: StateContext<PlantItemStateModel>,
    { payload }: PlantItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.plantItemService.deletePlant(payload.plantId).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.plant.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/plants');
        return ctx.dispatch(new PartnerItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PlantItemInitialState)
  setInitialState(ctx: StateContext<PlantItemStateModel>): void {
    ctx.setState(initialState);
  }
}
